import ActionButton from 'components/ActionButton';
import CategoriesDropwdown from 'components/CategoriesDropwdown';
import Currency from 'components/Currency/Currency';
import ImageUploadComponent from 'components/ImageUploadComponent';
import InventoryItemUnitDropwdown from 'components/InventoryItemUnitDropdown';
import { updateInventoryItemNutritionalFactImage } from 'services/api/inventoryItems';
import { Loading } from 'components/loading/Loading';
import React from 'react';
import styles from './index.module.scss';
import ItemAddress from './ItemAddress';
import _ from 'lodash';
import ItemWebURL from './itemWebUrl';
import DropDown from 'components/FormFields/dropdown';
import { INVENTORY_ITEM_PACKAGING_TYPE, USER_ROLES, isUserAuthorized } from 'utils';
import DynamicLink from 'components/DynamicLink';
import ImageWrapper from 'components/ImageWrapper/index';

const ItemBasicDetails = props => {
  const {
    activeLoading,
    editInfo,
    excludedLoading,
    detailsLoading,
    handleEditCancel,
    itemDetails,
    itemId,
    setAdjustQuantityModalOpen,
    setAdjustUnfulfilledQuantityModalOpen,
    setEditInfo,
    setUpdatedItem,
    setViewImageModalOpen,
    toggleHomeBoxInclusionFn,
    toggleInventoryItemActive,
    updatedItem,
    updateInventoryItemDetails,
    updateInventoryItemImage,
    viewImageModalOpen,
    userRoles,
  } = props;
  const isUserAuthorizedToEdit = isUserAuthorized(userRoles, [USER_ROLES.PACKER, USER_ROLES.SUPERVISOR, USER_ROLES.MANAGER, USER_ROLES.ADMIN]);
  const enWebAppUrl = `${process.env.REACT_APP_WEB_APP_URL}/items/${itemDetails.enSlug}`;
  const esWebAppUrl = `${process.env.REACT_APP_WEB_APP_URL}/items/${itemDetails.slug}`;

  const itemInStock = () => itemDetails.stock > 0;

  const handleEditCancelFn = () => {
    handleEditCancel(_.omit(itemDetails, [
      'name',
      'description',
      'englishName',
      'englishDescription',
    ]));
  };

  const getItemAddressInfo = () => {
    const handleAddressChange = (key: string, addressObject: Record<string, any>) => {
      setUpdatedItem({
        ...updatedItem,
        inventoryAddress: {
          ...updatedItem.inventoryAddress,
          [key]: addressObject,
        },
      });
    };

    return (
      <div className={styles['item-address-container']}>
        <div>
          <ItemAddress
            addressType={'Start'}
            inventoryAddress={updatedItem.inventoryAddress?.start}
            editInfo={editInfo}
            loading={detailsLoading}
            setAddress={handleAddressChange}
          />
        </div>
        <div>
          <ItemAddress
            addressType={'End'}
            inventoryAddress={updatedItem.inventoryAddress?.end}
            editInfo={editInfo}
            loading={detailsLoading}
            setAddress={handleAddressChange}
          />
        </div>
        <div className={styles['headerLabel']}>
          <div className={styles['label']}>Web App URLs</div>
          <div className="d-flex flex-column">
            <ItemWebURL labelText={'Spanish URL:'} url={esWebAppUrl} customClass={'mt-10'} />
            <ItemWebURL labelText={'English URL:'} url={enWebAppUrl} />
          </div>
        </div>
        <div className={styles['headerLabel']}>
          <DynamicLink link={itemDetails?.dynamicLink} />
        </div>
      </div>
    );
  };

  const getNutritionalFactImage = () => (
    <div className={styles['item-nutritionalFactImage']}>
      <div className={styles['label-container']}>
        <div className={styles['label']}>Nutritional Fact</div>
      </div>
      <div className={styles['image-container']}>
        <ImageUploadComponent
          entityId={itemId}
          toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
          src={itemDetails.nutritionalFactImage?.url}
          uploadAPI={updateInventoryItemNutritionalFactImage}
        />
      </div>
    </div>
  );

  const getAdjustQuantityButton = () => {
    if (!isUserAuthorizedToEdit) {
      return null;
    }
    return <ActionButton active={true} isLoading={false} text={'Adjust Quantity'} onClick={() => setAdjustQuantityModalOpen(true)} />;
  };

  const getItemBasicDetails = () => {
    const getMarginPercent = () => {
      const margin = itemDetails.price - itemDetails.weightedAvgCost;
      return (margin * 100 / itemDetails.weightedAvgCost).toFixed(2);
    };

    const getQuantity = () => (
      <div className={styles['item-quantity']}>
        <div>
          <div className={styles['label']}>Quantity</div>
          <div className={styles['info']}>{itemDetails.stock}</div>
        </div>
        {getAdjustQuantityButton()}
      </div>
    );

    const getPackagingType = () => (
      <div className={styles['item-packagingType']}>
        {!editInfo ?
          (<div>
            <div className={styles['label']}>Packaging Type</div>
            {itemDetails?.packagingType ?? 'N/A'}
          </div>) : (
            <DropDown
              title="Packaging Type"
              value={updatedItem.packagingType}
              onChange={packagingType => setUpdatedItem({
                ...updatedItem,
                packagingType,
              })}
              options={INVENTORY_ITEM_PACKAGING_TYPE}
            />)}
      </div>
    );

    const getExcludedCheckbox = () => (
      <div className={styles['item-excluded']}>
        <div className={styles['active-input-container']}>
          {excludedLoading ? (
            <Loading type={'mini'} />
          ) : (
            <input
              type={'checkbox'}
              disabled={excludedLoading || !isUserAuthorizedToEdit}
              onChange={toggleHomeBoxInclusionFn}
              checked={itemDetails.excludedFromHomeBox}
            />
          )}
        </div>
        <div className={styles['label']}>Excluded From Home Box</div>
      </div>
    );

    const getActiveCheckbox = () => (
      <div className={styles['item-active']}>
        <div className={styles['label']}>Active</div>
        <div className={styles['active-input-container']}>
          {activeLoading ? (
            <Loading type={'mini'} />
          ) : (
            <input
              type={'checkbox'}
              disabled={activeLoading || !isUserAuthorizedToEdit}
              checked={itemDetails.active}
              onChange={toggleInventoryItemActive}
            />
          )}
        </div>
      </div>
    );

    const getMarginPercentage = () => itemDetails.weightedAvgCost ? (
      <div className={styles['item-marginPercent']}>
        <div className={styles['label']}>Margin %</div>
        <div className={styles['info']}>{getMarginPercent()}</div>
      </div>
    ) : null;

    const getMarginValue = () => itemDetails.weightedAvgCost ? (
      <div className={styles['item-margin']}>
        <div className={styles['label']}>Margin</div>
        <div className={styles['info']}><Currency amount={parseFloat((itemDetails.price - itemDetails.weightedAvgCost).toFixed(2))} /></div>
      </div>
    ) : null;

    const getCost = () => (
      <div className={styles['item-cost']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Cost</div>
            <div className={styles['info']}><Currency amount={itemDetails.weightedAvgCost ? parseFloat(itemDetails.weightedAvgCost?.toFixed(2)) : undefined} /></div>
          </div>
        ) : (
          <input
            className={'form-control w-100'}
            type={'number'}
            min={0}
            placeholder={'Weighted Avg cost'}
            disabled={detailsLoading}
            value={updatedItem.weightedAvgCost}
            onChange={e => setUpdatedItem({
              ...updatedItem,
              weightedAvgCost: e.target.value ? parseFloat(e.target.value) : undefined,
            })}
          />
        )}
      </div>
    );

    const getHomeBoxProbability = () => (
      <div className={styles['item-probability']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Home Box Probability</div>
            <div>
              {itemDetails.probability !== undefined ? (
                <div>{itemDetails.probability.toString()}</div>
              ) : 'N/A'}
            </div>
          </div>
        ) : (
          <input
            className={'form-control w-100'}
            type={'number'}
            min={0}
            max={100}
            placeholder={'Home Box Probability'}
            disabled={detailsLoading}
            value={updatedItem.probability}
            onChange={e => setUpdatedItem({
              ...updatedItem,
              probability: parseInt(e.target.value),
            })}
          />
        )}
      </div>
    );

    const getAdjustUnfulfilledQuantityButton = () => {
      if (!isUserAuthorizedToEdit) {
        return null;
      }
      return <ActionButton active={true} isLoading={false} text={'Adjust Quantity'} onClick={() => setAdjustUnfulfilledQuantityModalOpen(true)} />;
    };

    const getUnfulfilledStock = () => (
      <div className={styles['item-quantity']}>
        <div>
          <div className={styles['label']}>Unfulfilled stock</div>
          <div>{itemDetails.unfulfilledStock || '0'}</div>
        </div>
        {getAdjustUnfulfilledQuantityButton()}
      </div>
    );

    const getItemUnit = () => (
      <div className={styles['item-unit']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Unit</div>
            <div>{itemDetails.unit?.description}</div>
          </div>
        ) : (
          <InventoryItemUnitDropwdown
            loading={detailsLoading}
            value={updatedItem.unit.objectId}
            onSelect={unit => setUpdatedItem({
              ...updatedItem,
              unit,
            })}
          />
        )}
      </div>
    );

    const getItemCategory = () => (
      <div className={styles['item-category']}>
        {editInfo ? (
          <CategoriesDropwdown
            disabled={detailsLoading}
            value={updatedItem.category.objectId}
            onSelect={category => setUpdatedItem({
              ...updatedItem,
              category,
            })}
          />
        ) : (
          <>
            <div className={styles['label']}>Category</div>
            <div className={styles['category-label']}>{itemDetails.category?.description}</div>
          </>
        )}
      </div>
    );

    const getItemPrice = () => (
      <div className={styles['item-price']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Price</div>
            <div className={styles['info']}><Currency amount={itemDetails.price} /></div>
          </div>
        ) : (
          <input
            className={'form-control w-100'}
            type={'number'}
            min={0}
            placeholder={'Price'}
            disabled={detailsLoading}
            value={updatedItem.price}
            onChange={e => setUpdatedItem({
              ...updatedItem,
              price: parseFloat(e.target.value),
            })}
          />
        )}
      </div>
    );

    const getItemId = () => (
      <div className={styles['item-id']}>
        <div className={styles['label']}>ItemID</div>
        <div className={styles['info']}>{itemDetails.objectId}</div>
      </div>
    );

    const getStockLabel = () => (
      <div className={`${styles['stock-label']} ${styles[itemInStock() ? 'stock' : 'empty']}`}>
        {itemInStock() ? 'in stock' : 'out of stock'}
      </div>
    );

    const getItemName = () => (
      <div>
        <div className={styles['label']}>Item Name</div>
        <div className={styles['info']}>{itemDetails.name}</div>
      </div>
    );

    const getOrigin = () => (
      <div className={styles['item-origin']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Origin</div>
            <div>{itemDetails.origin || 'None'}</div>
          </div>
        ) : (
          <select
            value={updatedItem?.origin}
            defaultValue=''
            disabled={detailsLoading}
            onChange={e => {
              setUpdatedItem({
                ...updatedItem,
                origin: e.target?.value,
              });
            }}
            className={'form-control w-100'}
          >
            <option key={'1'} value={''}>Select</option>
            <option key={'2'} value={'LOCAL'}>Local</option>
            <option key={'3'} value={'IMPORTED'}>Imported</option>
          </select>
        )}
      </div>
    );

    const getPromotionalStatus = () => (
      <div className={styles['item-promotional']}>
        <div className={styles['active-input-container']}>
          <input
            type={'checkbox'}
            disabled={!editInfo || detailsLoading || !isUserAuthorizedToEdit}
            checked={updatedItem.isPromotional}
            onChange={(e) => {
              setUpdatedItem({
                ...updatedItem,
                isPromotional: e.target.checked,
              });
            }}
          />
        </div>
        <div className={styles['label']}>Promotional</div>
      </div>
    );

    const getFeaturedStatus = () => (
      <div className={styles['item-featured']}>
        <div className={styles['active-input-container']}>
          <input
            type={'checkbox'}
            disabled={!editInfo || detailsLoading || !isUserAuthorizedToEdit}
            checked={updatedItem.isFeatured}
            onChange={(e) => {
              setUpdatedItem({
                ...updatedItem,
                isFeatured: e.target.checked,
              });
            }}
          />
        </div>
        <div className={styles['label']}>Featured</div>
      </div>
    );

    const getImageWithoutBg = () => {
      const imageUrl = itemDetails?.imageWithoutBackground?.url;
      if (!imageUrl) return null;
      return (
        <ImageWrapper 
          className={styles['image-without-bg']} 
          src={itemDetails.imageWithoutBackground?.url} 
        />
      );
    };

    const getEditActions = () => {
      if (!isUserAuthorizedToEdit) {
        return null;
      }
      return (
        <div className={styles['buttons']}>
          {editInfo ? (
            <>
              <ActionButton
                active={!detailsLoading}
                isLoading={detailsLoading}
                onClick={updateInventoryItemDetails}
                text={'Save'}
              />
              <ActionButton
                active={true}
                isLoading={false}
                onClick={handleEditCancelFn}
                text={'Cancel'}
                customClass={styles['cancel-button']}
              />
            </>
          ) : (
            <ActionButton
              active={true}
              isLoading={false}
              onClick={() => setEditInfo(!editInfo)}
              text={'Edit'}
            />
          )}
        </div>
      );
    };

    const getItemReorderQuantity = () => (
      <div className={styles['item-price']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Reorder Quantity</div>
            <div className={styles['info']}>{itemDetails.reorderQuantity || '-'}</div>
          </div>
        ) : (
          <input
            className={'form-control w-100'}
            type={'number'}
            min={0}
            placeholder={'Reorder Quantity'}
            disabled={detailsLoading}
            value={updatedItem.reorderQuantity}
            onChange={e => setUpdatedItem({
              ...updatedItem,
              reorderQuantity: parseInt(e.target.value),
            })}
          />
        )}
      </div>
    );
  
    const getItemReorderThreshold = () => (
      <div className={styles['item-price']}>
        {!editInfo ? (
          <div>
            <div className={styles['label']}>Reorder Threshold</div>
            <div className={styles['info']}>{itemDetails.reorderThreshold || '-'}</div>
          </div>
        ) : (
          <input
            className={'form-control w-100'}
            type={'number'}
            min={0}
            placeholder={'Reorder Threshold'}
            disabled={detailsLoading}
            value={updatedItem.reorderThreshold}
            onChange={e => setUpdatedItem({
              ...updatedItem,
              reorderThreshold: parseInt(e.target.value),
            })}
          />
        )}
      </div>
    );
  
    const getAutomaticReorderCheckbox = () => (
      <div className={styles['item-active']}>
        <div className={styles['label']}>Automatic Reorder</div>
        <div className={styles['active-input-container']}>
          <input
            type={'checkbox'}
            disabled={detailsLoading || !editInfo}
            checked={updatedItem.automaticReorder}
            onChange={(e)=>{
              setUpdatedItem({
                ...updatedItem,
                automaticReorder: !updatedItem.automaticReorder,
              });
            }}
          />
        </div>
      </div>
    );

    const getMustHaveField = () => (
      <div className={styles['item-featured']}>
        <div className={styles['active-input-container']}>
          <input
            type={'checkbox'}
            disabled={!editInfo || detailsLoading || !isUserAuthorizedToEdit}
            checked={updatedItem.mustHave}
            onChange={(e) => {
              setUpdatedItem({
                ...updatedItem,
                mustHave: e.target.checked,
              });
            }}
          />
        </div>
        <div className={styles['label']}>Must Have</div>
      </div>
    );
  
    const itemReorderInfo = () => {
      return (
        <div className={'d-flex flex-wrap'}>
          {getAutomaticReorderCheckbox()}
          {getItemReorderThreshold()}
          {getItemReorderQuantity()}
        </div>
      );
    };
    
    return (
      <div className={styles['image-and-info-fields-container']}>
        <div className={styles['image-container']}>
          <ImageUploadComponent
            entityId={itemId}
            toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
            src={itemDetails.image?.url}
            uploadAPI={updateInventoryItemImage}
          />
          {getImageWithoutBg()}
        </div>
        <div className={styles['info-fields-container']}>
          <div className={styles['item-name-and-edit-action-container']}>
            <div className={styles['item-name-and-stock']}>
              {getItemName()}
              {getStockLabel()}
            </div>
            {getEditActions()}
          </div>
          <div className={styles['info-fields-section']}>
            <div className={'d-flex flex-wrap'}>
              {getItemId()}
              {getItemCategory()}
              {getItemUnit()}
              {getUnfulfilledStock()}
              {getItemPrice()}
              {getCost()}
              {getMarginValue()}
              {getMarginPercentage()}
              {getActiveCheckbox()}
              {getExcludedCheckbox()}
              {getPromotionalStatus()}
              {getFeaturedStatus()}
              {getMustHaveField()}
              {getHomeBoxProbability()}
              {getOrigin()}
              {getQuantity()}
              {getPackagingType()}
            </div>
            {itemReorderInfo()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['basic-details-container']}>
      {getItemBasicDetails()}
      {getNutritionalFactImage()}
      {getItemAddressInfo()}
    </div>
  );
};

export default ItemBasicDetails;